/**
 * Alert rules pane — build nested AND/OR conditions over any field of a feed.
 *
 * The builder is recursive: a group holds conditions and further groups, so "premium ≥ 1M AND
 * (side is Ask OR DTE ≤ 7)" is expressible without a special case. Editing a rule clears its fired
 * history, otherwise a tightened rule would stay silent on rows it had already matched.
 */

import React, { useMemo, useState } from 'react';
import './AlertRulesPanel.css';
import {
  addToGroup,
  AlertCondition,
  AlertFieldDef,
  AlertGroup,
  AlertNode,
  AlertOp,
  ALERT_OP_LABELS,
  AlertRule,
  AlertSourceDef,
  countConditions,
  describeNode,
  emptyGroup,
  newCondition,
  newRule,
  opNeedsValue,
  opsForFieldType,
  removeNode,
  updateNode,
} from '../../utils/alertRules';
import { useAlertRuleStore } from '../../hooks/useAlertRules';
import { requestBrowserPermission, resetRuleHistory, playAlertSound } from '../../utils/alertRuntime';
import { presetsForSource, ruleFromPreset, UNPORTED_LEGACY_PRESETS } from '../../utils/alertPresets';
import AlertLog from './AlertLog';

export interface AlertRulesPanelProps {
  source: AlertSourceDef;
  /** Rendered inline (as a right-hand pane) by default. */
  onClose?: () => void;
}

const AlertRulesPanel: React.FC<AlertRulesPanelProps> = ({ source, onClose }) => {
  const [rules, setRules] = useAlertRuleStore();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [tab, setTab] = useState<'rules' | 'history'>('rules');

  const fieldMap = useMemo(
    () =>
      source.fields.reduce(
        (acc, f) => {
          acc[f.key] = f;
          return acc;
        },
        {} as Record<string, AlertFieldDef>,
      ),
    [source.fields],
  );

  const mine = rules.filter((r) => r.sourceId === source.id);

  const commit = (next: AlertRule[]) => setRules(next);

  const patchRule = (id: string, patch: Partial<AlertRule>) => {
    resetRuleHistory(id);
    commit(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const patchRoot = (rule: AlertRule, root: AlertGroup) => patchRule(rule.id, { root });

  const addRule = () => {
    const rule = newRule(source.id, source.fields[0], `${source.label} alert ${mine.length + 1}`);
    commit([...rules, rule]);
    setExpanded(rule.id);
  };

  const removeRule = (id: string) => {
    resetRuleHistory(id);
    commit(rules.filter((r) => r.id !== id));
  };

  const renderNode = (rule: AlertRule, node: AlertNode, depth: number): React.ReactNode => {
    if (node.kind === 'group') {
      return (
        <div className={`arGroup depth${Math.min(depth, 3)}`} key={node.id}>
          <div className="arGroupHead">
            <div className="arLogic">
              {(['AND', 'OR'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`chip${node.logic === l ? ' on' : ''}`}
                  onClick={() => patchRoot(rule, updateNode(rule.root, node.id, { logic: l }))}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="arGroupActions">
              <button
                type="button"
                className="chip"
                onClick={() =>
                  patchRoot(
                    rule,
                    addToGroup(rule.root, node.id, newCondition(source.fields[0].key, source.fields[0].type)),
                  )
                }
              >
                + Condition
              </button>
              <button
                type="button"
                className="chip"
                onClick={() => patchRoot(rule, addToGroup(rule.root, node.id, emptyGroup(node.logic === 'AND' ? 'OR' : 'AND')))}
              >
                + Group
              </button>
              {depth > 0 && (
                <button type="button" className="cfdel" aria-label="remove group" onClick={() => patchRoot(rule, removeNode(rule.root, node.id))}>
                  ×
                </button>
              )}
            </div>
          </div>
          {!node.children.length && <div className="arHint">Empty groups never match — add a condition.</div>}
          {node.children.map((child) => renderNode(rule, child, depth + 1))}
        </div>
      );
    }

    const cond = node as AlertCondition;
    const def = fieldMap[cond.field];
    const type = def?.type ?? 'text';
    return (
      <div className="arCond" key={cond.id}>
        <select
          className="sel"
          value={cond.field}
          onChange={(e) => {
            const nextDef = fieldMap[e.target.value];
            patchRoot(
              rule,
              updateNode(rule.root, cond.id, {
                field: e.target.value,
                op: opsForFieldType(nextDef?.type ?? 'text')[0],
                value: '',
              }),
            );
          }}
        >
          {source.fields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          className="sel"
          value={cond.op}
          onChange={(e) => patchRoot(rule, updateNode(rule.root, cond.id, { op: e.target.value as AlertOp }))}
        >
          {opsForFieldType(type).map((op) => (
            <option key={op} value={op}>
              {ALERT_OP_LABELS[op]}
            </option>
          ))}
        </select>
        {opNeedsValue(cond.op) &&
          (type === 'enum' && def?.options?.length ? (
            <select
              className="sel"
              value={cond.value}
              onChange={(e) => patchRoot(rule, updateNode(rule.root, cond.id, { value: e.target.value }))}
            >
              <option value="">choose…</option>
              {def.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="num mono"
              type={type === 'number' ? 'number' : 'text'}
              placeholder={type === 'number' ? 'value…' : 'text…'}
              value={cond.value}
              onChange={(e) => patchRoot(rule, updateNode(rule.root, cond.id, { value: e.target.value }))}
            />
          ))}
        <button type="button" className="cfdel" aria-label="remove condition" onClick={() => patchRoot(rule, removeNode(rule.root, cond.id))}>
          ×
        </button>
      </div>
    );
  };

  return (
    <aside className="arPanel">
      <div className="arHead">
        <span className="lab">Alerts · {source.label}</span>
        <div className="ctl">
          <button type="button" className="chip" onClick={addRule}>
            + Rule
          </button>
          {onClose && (
            <button type="button" className="cfdel" aria-label="close" onClick={onClose}>
              ×
            </button>
          )}
        </div>
      </div>

      <div className="arSourceTabs">
        <button type="button" className={`chip${tab === 'rules' ? ' on' : ''}`} onClick={() => setTab('rules')}>
          Rules
        </button>
        <button type="button" className={`chip${tab === 'history' ? ' on' : ''}`} onClick={() => setTab('history')}>
          History
        </button>
      </div>

      {tab === 'history' && <AlertLog />}

      {tab === 'rules' && (
      <div className="arPresets">
        <button type="button" className="chip" onClick={() => setPresetsOpen((v) => !v)}>
          {presetsOpen ? 'Hide presets' : 'Browse presets'}
        </button>
        {presetsOpen && (
          <div className="arPresetList">
            {presetsForSource(source.id).map((cat) => (
              <div className="arPresetCat" key={cat.key}>
                <div className="arPresetCatName">{cat.name}</div>
                <div className="arHint">{cat.description}</div>
                {cat.presets.map((preset) => (
                  <button
                    type="button"
                    className="arPresetBtn"
                    key={preset.legacyId}
                    title={preset.note || 'Add this preset as an editable rule'}
                    onClick={() => {
                      const rule = ruleFromPreset(preset);
                      commit([...rules, rule]);
                      setExpanded(rule.id);
                      setPresetsOpen(false);
                    }}
                  >
                    + {preset.name}
                    {preset.note && <span className="arPresetNote">{preset.note}</span>}
                  </button>
                ))}
              </div>
            ))}
            {!presetsForSource(source.id).length && (
              <div className="arHint">No presets for this feed — build a rule instead.</div>
            )}
            {source.id === 'market-signals' && (
              <div className="arHint">
                Not carried over: {UNPORTED_LEGACY_PRESETS.map((p) => p.name).join(', ')} — these are
                trading-call events rather than anything derivable from scanner data.
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {tab === 'rules' && !mine.length && (
        <div className="arHint">
          No alerts here yet. A rule watches this feed and fires once per matching opportunity —
          popup, sound, or a desktop notification.
        </div>
      )}

      {tab === 'rules' && mine.map((rule) => {
        const open = expanded === rule.id;
        return (
          <div className={`arRule${rule.enabled ? '' : ' off'}`} key={rule.id}>
            <div className="arRuleHead">
              <label className="arToggle">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(e) => {
                    if (e.target.checked && rule.actions.browser) requestBrowserPermission();
                    patchRule(rule.id, { enabled: e.target.checked });
                  }}
                />
                <input
                  className="arName"
                  value={rule.name}
                  onChange={(e) => patchRule(rule.id, { name: e.target.value })}
                />
              </label>
              <button type="button" className="chip" onClick={() => setExpanded(open ? null : rule.id)}>
                {open ? 'Done' : 'Edit'}
              </button>
              <button type="button" className="cfdel" aria-label="delete rule" onClick={() => removeRule(rule.id)}>
                ×
              </button>
            </div>

            <div className="arSummary mono">{describeNode(rule.root, fieldMap)}</div>

            {open && (
              <div className="arBody">
                {renderNode(rule, rule.root, 0)}

                <div className="arActions">
                  <span className="lab">When it fires</span>
                  <div className="ctl">
                    {(
                      [
                        ['popup', 'Popup'],
                        ['sound', 'Sound'],
                        ['browser', 'Desktop'],
                      ] as const
                    ).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={`chip${rule.actions[key] ? ' on' : ''}`}
                        onClick={() => {
                          const next = { ...rule.actions, [key]: !rule.actions[key] };
                          if (key === 'browser' && next.browser) requestBrowserPermission();
                          patchRule(rule.id, { actions: next });
                        }}
                      >
                        {label}
                      </button>
                    ))}
                    <button type="button" className="chip" onClick={() => playAlertSound(rule.actions.soundFile)}>
                      Test ♪
                    </button>
                  </div>
                </div>

                <div className="arActions">
                  <span className="lab">Quiet for</span>
                  <div className="ctl">
                    <input
                      className="num mono"
                      type="number"
                      min={0}
                      value={rule.throttleSec}
                      onChange={(e) => patchRule(rule.id, { throttleSec: Math.max(0, Number(e.target.value) || 0) })}
                    />
                    <span className="arHint inline">seconds between alerts from this rule</span>
                  </div>
                </div>

                <div className="arHint">
                  {countConditions(rule.root)} condition{countConditions(rule.root) === 1 ? '' : 's'} · fires once per
                  matching {source.label.toLowerCase().includes('tape') ? 'print' : 'opportunity'}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
};

export default AlertRulesPanel;
